/**
 * What the inbox search is looking for, on the screen side (contract v10.21).
 *
 * The back decides which emails match (`cubas.domain.search` in `Cubas_Back`); this only
 * finds the same words again in what the row already shows, so they can be marked. The
 * rules are the back's, restated for the one thing the screen needs: the words to mark.
 * Get one wrong here and a word is not marked; the list itself stays right.
 *
 * - words split on spaces; a phrase between quotes (straight, “”, «») is one term;
 * - a word with a dash in front is excluded, so there is nothing to mark;
 * - accents and capitals never matter: «camion» marks «Camión».
 */

const QUOTES = new Set(['"', '“', '”', '«', '»', '„']);
const EDGE = /^[.,;:!?¿¡()[\]{}<>'`"“”«»„]+|[.,;:!?¿¡()[\]{}<>'`"“”«»„]+$/g;

/** Lowercase, without accents, spaces collapsed: the back's `normalize`. */
export function fold(text: string): string {
  return text.normalize('NFKD').replace(/\p{M}/gu, '').replace(/\s+/g, ' ').trim().toLowerCase();
}

/** The terms of a search that have to appear, folded. Exclusions are left out. */
export function includedTerms(raw: string): string[] {
  const terms: string[] = [];
  let i = 0;
  while (i < raw.length) {
    const char = raw.charAt(i);
    if (/\s/.test(char)) {
      i += 1;
      continue;
    }
    const negate = char === '-' && i + 1 < raw.length && !/\s/.test(raw.charAt(i + 1));
    if (negate) i += 1;
    let term: string;
    if (QUOTES.has(raw.charAt(i))) {
      let end = i + 1;
      while (end < raw.length && !QUOTES.has(raw.charAt(end))) end += 1;
      term = fold(raw.slice(i + 1, end));
      i = end + 1;
    } else {
      let end = i;
      while (end < raw.length && !/\s/.test(raw.charAt(end))) end += 1;
      term = fold(raw.slice(i, end)).replace(EDGE, '');
      i = end;
    }
    if (!negate && /[\p{L}\p{N}]/u.test(term) && !terms.includes(term)) terms.push(term);
  }
  return terms;
}

export interface Piece {
  text: string;
  match: boolean;
}

/**
 * `text` cut into the pieces that match one of `terms` and the pieces that do not, as
 * written: the accents stay on screen, only the comparison ignores them.
 *
 * Each character is folded on its own and remembers where it came from, so a match in
 * the folded text maps back to the exact characters of the original. Whitespace runs
 * fold to one space, like the back's fold, so «precio  neto» still matches a phrase.
 */
export function highlight(text: string, terms: string[]): Piece[] {
  if (!text || terms.length === 0) return text ? [{ text, match: false }] : [];
  let folded = '';
  const origin: number[] = [];
  let previousSpace = false;
  for (let index = 0; index < text.length; index += 1) {
    const char = text.charAt(index);
    if (/\s/.test(char)) {
      if (!previousSpace) {
        folded += ' ';
        origin.push(index);
      }
      previousSpace = true;
      continue;
    }
    previousSpace = false;
    for (const piece of fold(char)) {
      folded += piece;
      origin.push(index);
    }
  }

  const marked = new Array<boolean>(text.length).fill(false);
  for (const term of terms) {
    let from = folded.indexOf(term);
    while (from !== -1) {
      const first = origin[from] ?? 0;
      const last = origin[from + term.length - 1] ?? first;
      for (let index = first; index <= last; index += 1) marked[index] = true;
      from = folded.indexOf(term, from + term.length);
    }
  }

  const pieces: Piece[] = [];
  for (let index = 0; index < text.length; index += 1) {
    const match = marked[index] ?? false;
    const last = pieces.at(-1);
    if (last && last.match === match) last.text += text.charAt(index);
    else pieces.push({ text: text.charAt(index), match });
  }
  return pieces;
}
